import * as FileSaver from 'file-saver'
import moment from 'moment'
import * as XLSX from 'xlsx-js-style'
import { capitalizeEveryWord } from 'helpers'
import colors from 'Theme/colors'

// ---------------------------------------------------------------------------
// Palette: only the project theme colours (Theme/colors), same as the page
// ---------------------------------------------------------------------------
const hex = (value) => value.replace('#', '').toUpperCase()

const COLOR = {
  navy: hex(colors.neutral[800]),
  slate: hex(colors.neutral[700]),
  muted: hex(colors.neutral[500]),
  faint: hex(colors.neutral[400]),
  line: hex(colors.neutral[200]),
  zebra: hex(colors.neutral[50]),
  gray: hex(colors.neutral[100]),
  white: hex(colors.white),
  blue: hex(colors.primary[500]),
  blueDark: hex(colors.primary[600]),
  blueSoft: hex(colors.primary[25]),
  green: hex(colors.success[600]),
  greenSoft: hex(colors.success[50]),
  orange: hex(colors.warning[700]),
  orangeSoft: hex(colors.warning[50]),
}

const STATUS_COLOR = {
  'Dalam Stok': hex(colors.success[600]),
  'Sudah Keluar': hex(colors.warning[600]),
  'Hapus - Jual': hex(colors.error[600]),
  'Hapus - Buang': hex(colors.neutral[500]),
}

const FONT = 'Calibri'

const toNum = (n) => {
  const value = Number(n)
  return Number.isFinite(value) ? value : 0
}

const formatUnit = (unit) => capitalizeEveryWord(unit || '')

const solid = (hex) => ({ patternType: 'solid', fgColor: { rgb: hex } })

const box = (hex = COLOR.line) => {
  const side = { style: 'thin', color: { rgb: hex } }
  return { top: side, bottom: side, left: side, right: side }
}

const font = ({ color = COLOR.slate, ...rest } = {}) => ({
  name: FONT,
  sz: 10,
  ...rest,
  color: { rgb: color },
})

const align = (horizontal = 'left', wrapText = false) => ({
  horizontal,
  vertical: 'center',
  wrapText,
  ...(horizontal === 'left' ? { indent: 1 } : {}),
})

// Create the cell when it does not exist yet, then merge the style into it
const styleCell = (sheet, r, c, style) => {
  const address = XLSX.utils.encode_cell({ r, c })
  if (!sheet[address]) sheet[address] = { t: 's', v: '' }
  sheet[address].s = { ...(sheet[address].s || {}), ...style }
}

const styleRange = (sheet, r1, c1, r2, c2, style) => {
  for (let r = r1; r <= r2; r += 1) {
    for (let c = c1; c <= c2; c += 1) {
      styleCell(sheet, r, c, style)
    }
  }
}

// Real numeric cells (so Excel can sum them) with a thousand separator
const applyNumberFormat = (sheet) => {
  Object.keys(sheet).forEach((key) => {
    if (key[0] === '!') return
    const cell = sheet[key]
    if (cell && cell.t === 'n') {
      cell.z = Number.isInteger(cell.v) ? '#,##0' : '#,##0.00'
    }
  })
}

const mergeRow = (r, c1, c2) => ({ s: { r, c: c1 }, e: { r, c: c2 } })

// Title block shared by both sheets (rows 0-2)
const styleTitleBlock = (sheet, totalCols) => {
  styleRange(sheet, 0, 0, 0, totalCols - 1, {
    font: font({ sz: 16, bold: true, color: COLOR.white }),
    fill: solid(COLOR.navy),
    alignment: align('left'),
  })
  styleRange(sheet, 1, 0, 1, totalCols - 1, {
    font: font({ sz: 11, bold: true, color: COLOR.slate }),
    fill: solid(COLOR.gray),
    alignment: align('left'),
  })
  styleRange(sheet, 2, 0, 2, totalCols - 1, { fill: solid(COLOR.blue) })
}

/**
 * Export of the traceability detail page. It mirrors what the page shows:
 *   - the flow always reads left -> right (Bahan Masuk -> Barang Jadi)
 *       BACKWARD: Asal Barang -> Lot Ini
 *       FORWARD : Lot Ini     -> Tujuan Barang
 *   - sheet "Ketelusuran": the connected inventories row by row
 *   - sheet "Ringkasan Konversi": Bahan Masuk - Barang Jadi = selisih,
 *     percentage and the per-item breakdown.
 */
export const handleDownloadExcel = (
  {
    displayedRows = [],
    ownerUserId,
    direction = 'FORWARD',
    lotCode = '-',
    dateLabel = '-',
    typeLabel = '-',
    summary = {},
    bahanItems = [],
    barangJadiItems = [],
  },
  setIsDownload,
  setFirstRows,
  setTotalRows,
  escrowpaymentId,
) => {
  setIsDownload(false)
  setFirstRows(0)
  setTotalRows(0)

  setTimeout(() => {
    setIsDownload(true)

    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    const bookType = 'xlsx'
    const wb = XLSX.utils.book_new()

    const isBackward = direction === 'BACKWARD'
    const leftTitle = isBackward ? 'Asal Barang' : 'Lot Ini'
    const rightTitle = isBackward ? 'Lot Ini' : 'Tujuan Barang'
    const directionLabel = isBackward ? 'Asal Barang' : 'Tujuan Barang'
    const createdAt = `Dibuat ${moment().format('DD MMM YYYY HH:mm')}`

    setTotalRows(displayedRows.length)

    // ---- Status / keterangan: same rules as the page ----
    const getStatusLabel = (s) => {
      if (!s || s?.isBlank) return '-'
      if (Number(s.ownerUserId) === 0) {
        if (
          s.extraData &&
          typeof s.extraData === 'string' &&
          s.extraData.includes('buyerName')
        ) {
          return 'Hapus - Jual'
        }
        return 'Hapus - Buang'
      }
      // ownerUserId is the current owner (ownerUserIds is only history)
      return String(s.ownerUserId) === String(ownerUserId)
        ? 'Dalam Stok'
        : 'Sudah Keluar'
    }

    const getKeterangan = (s) => {
      if (!s || s?.isBlank) return '-'
      const extraData = (() => {
        try {
          return JSON.parse(s.extraData)
        } catch {
          return {}
        }
      })()
      const status = getStatusLabel(s)
      const warehouseNameRaw = s.warehouse?.name || '-'
      const isGudangMentioned = /\bgudang\b/i.test(warehouseNameRaw)
      const warehouseNameCleaned = warehouseNameRaw.replace(
        /\bgudang\b/gi,
        'Di Gudang',
      )

      return (
        {
          'Dalam Stok': isGudangMentioned
            ? warehouseNameCleaned
            : `Di Gudang ${warehouseNameRaw}`,
          'Sudah Keluar': `Penerima: ${s.ownerpartnership?.name || s.owner?.name || '-'}`,
          'Hapus - Jual': `Penerima: ${extraData?.buyerName || '-'}`,
          'Hapus - Buang': '',
        }[status] || ''
      )
    }

    // ---- Column blocks (Jumlah and Satuan are separate columns) ----
    const inventoryHeader = [
      'Lot ID',
      'Kode Inventori',
      'Jenis Barang',
      'Jumlah',
      'Satuan',
    ]
    const inventoryCells = (inv) => [
      lotCode,
      inv?.ownerCode || '-',
      inv?.item?.nameItem || '-',
      toNum(inv?.amount),
      formatUnit(inv?.unit || inv?.item?.unit),
    ]

    const summaryHeader = [
      'Lot ID',
      'Kode Inventori',
      'Jenis Barang',
      ...(isBackward ? [] : ['Status']),
      'Jumlah',
      'Satuan',
      'Keterangan',
    ]
    // BACKWARD: Jumlah is the stock of the first source of the material
    // (before any split); Jumlah Dipakai is what the process it went into used
    const summaryCells = (s, firstAmount, estimated) => {
      if (!s || s?.isBlank) {
        return Array(summaryHeader.length).fill('-')
      }
      const amount = isBackward ? (firstAmount ?? s?.initialAmount) : s?.amount
      return [
        s?.lotCode || s?.lotId || '-',
        s?.ownerCode || '-',
        s?.item?.nameItem || '-',
        ...(isBackward ? [] : [getStatusLabel(s)]),
        toNum(amount),
        formatUnit(s?.unit || s?.item?.unit),
        [
          getKeterangan(s),
          // see sourceAmounts: no schema to split the parent's amount
          estimated && 'Jumlah Dipakai perkiraan (skema tidak ditemukan)',
        ]
          .filter(Boolean)
          .join('; ') || '-',
      ]
    }

    // Left block | line | Metode | Jumlah Dipakai | Satuan | line | right block
    // "Lot Ini" is the single block of a group (merged over the group rows),
    // the other side lists the connected inventories, one per row.
    const leftHeader = isBackward ? summaryHeader : inventoryHeader
    const rightHeader = isBackward ? inventoryHeader : summaryHeader
    const middleHeader = ['Metode', 'Jumlah Dipakai', 'Satuan']
    const allHeaders = [...leftHeader, '', ...middleHeader, '', ...rightHeader]
    const totalCols = allHeaders.length
    const leftCount = leftHeader.length
    const lineA = leftCount
    const midStart = leftCount + 1
    const midEnd = midStart + middleHeader.length - 1
    const lineB = midEnd + 1
    const rightStart = lineB + 1
    const lotStart = isBackward ? rightStart : 0
    const lotEnd = isBackward ? totalCols - 1 : leftCount - 1
    const isLotCol = (c) => c >= lotStart && c <= lotEnd

    // One group per Lot Ini inventory; its connected inventories are the rows
    const invById = new Map()
    displayedRows.forEach((r) => {
      if (r?.inventory) invById.set(Number(r._inventoryId), r.inventory)
    })
    const getRelation = (r, inv) => {
      const summaryRow = r?.summary
      const sid = Number(summaryRow?.id)
      const d = (inv?.summaryInvData || []).find((x) => Number(x.id) === sid)
      if (!d) return ['', '', '']
      const unitText = formatUnit(
        summaryRow?.unit ||
          summaryRow?.item?.unit ||
          inv?.unit ||
          inv?.item?.unit,
      )
      // BACKWARD: what went into this finished good (see sourceAmounts)
      const used = r?.usedAmount ?? d.amountUsed
      return [
        r?.method || d.createMethodLabel || '',
        used ? toNum(Math.round(used * 1000) / 1000) : '',
        used ? unitText : '',
      ]
    }
    const groups = []
    displayedRows.forEach((r) => {
      if (r?.isFirstRow || groups.length === 0) {
        groups.push({
          inventory: r?.inventory || invById.get(Number(r?._inventoryId)),
          rows: [],
        })
      }
      groups[groups.length - 1].rows.push(r)
    })

    const LINE = '──────'
    const tableRows = []
    const groupInfo = []
    const topRows = [
      ['LAPORAN KETELUSURAN'],
      [`${directionLabel}  ·  Lot ${lotCode}`],
      [],
      ['Arah Penelusuran', directionLabel],
      ['Lot ID', lotCode],
      ['Tanggal', dateLabel],
      ['Tipe', typeLabel],
      ['Cara Baca', summary.readingGuide || ''],
      [],
    ]
    const infoRows = [3, 4, 5, 6, 7]
    const bannerRow = topRows.length
    const headerRow = bannerRow + 1
    const firstDataRow = headerRow + 1

    let doneRows = 0
    groups.forEach((g, gi) => {
      const start = firstDataRow + tableRows.length
      const count = g.rows.length
      const midIndex = Math.floor((count - 1) / 2)
      g.rows.forEach((r, k) => {
        const lotCells = k === 0 ? inventoryCells(g.inventory) : []
        const lotBlock = Array(inventoryHeader.length)
          .fill('')
          .map((_, i) => lotCells[i] ?? '')
        const many = summaryCells(r?.summary, r?.firstAmount, r?.estimated)
        const arrowIn = isBackward && k === midIndex ? `${LINE}►` : LINE
        const left = isBackward ? many : lotBlock
        const right = isBackward ? lotBlock : many
        tableRows.push([
          ...left,
          LINE,
          ...getRelation(r, g.inventory),
          isBackward ? arrowIn : `${LINE}►`,
          ...right,
        ])
        // data rows only: tableRows also holds the blank row between
        // groups, which made the progress go past 100%
        doneRows += 1
        setFirstRows(Math.min(doneRows, displayedRows.length))
      })
      groupInfo.push({ start, end: start + count - 1, count })
      if (gi < groups.length - 1) tableRows.push(Array(totalCols).fill(''))
    })
    const lastDataRow = headerRow + tableRows.length
    const footerRow = lastDataRow + 2

    // =======================================================================
    // Sheet 1: Ketelusuran
    // =======================================================================
    const colWidth = (h) =>
      ({
        'Lot ID': 16,
        'Kode Inventori': 18,
        'Jenis Barang': 22,
        Keterangan: 32,
        Jumlah: 12,
        'Jumlah Dipakai': 16,
        Satuan: 10,
        Metode: 18,
        Status: 15,
        '': 8,
      })[h] ?? 18

    const banner = Array(totalCols).fill('')
    banner[0] = isBackward ? 'ASAL BARANG' : 'LOT INI'
    banner[lineA] = 'MENJADI  ►'
    banner[rightStart] = isBackward ? 'LOT INI' : 'TUJUAN BARANG'

    const sheet = XLSX.utils.aoa_to_sheet([
      ...topRows,
      banner,
      allHeaders,
      ...tableRows,
      [],
      [
        `${createdAt}  ·  ${groups.length} lot  ·  ${displayedRows.length} baris data`,
      ],
    ])

    const groupMerges = []
    groupInfo.forEach(({ start, end }) => {
      if (end > start) {
        for (let c = lotStart; c <= lotEnd; c += 1) {
          groupMerges.push({ s: { r: start, c }, e: { r: end, c } })
        }
      }
    })
    sheet['!merges'] = [
      mergeRow(0, 0, totalCols - 1),
      mergeRow(1, 0, totalCols - 1),
      mergeRow(2, 0, totalCols - 1),
      ...infoRows.map((r) => mergeRow(r, 1, r === 7 ? totalCols - 1 : 3)),
      mergeRow(bannerRow, 0, leftCount - 1),
      mergeRow(bannerRow, lineA, lineB),
      mergeRow(bannerRow, rightStart, totalCols - 1),
      mergeRow(footerRow, 0, totalCols - 1),
      ...groupMerges,
    ]
    sheet['!cols'] = allHeaders.map((h) => ({ wch: colWidth(h) }))
    sheet['!rows'] = []
    sheet['!rows'][0] = { hpx: 34 }
    sheet['!rows'][1] = { hpx: 24 }
    sheet['!rows'][2] = { hpx: 4 }
    infoRows.forEach((r) => {
      sheet['!rows'][r] = { hpx: 20 }
    })
    sheet['!rows'][bannerRow] = { hpx: 26 }
    sheet['!rows'][headerRow] = { hpx: 24 }
    for (let r = firstDataRow; r <= lastDataRow; r += 1) {
      sheet['!rows'][r] = { hpx: 22 }
    }
    groupInfo.forEach(({ end }, gi) => {
      if (gi < groupInfo.length - 1) sheet['!rows'][end + 1] = { hpx: 8 }
    })

    // Title + info
    styleTitleBlock(sheet, totalCols)
    infoRows.forEach((r) => {
      styleCell(sheet, r, 0, {
        font: font({ bold: true, color: COLOR.muted }),
        fill: solid(COLOR.gray),
        alignment: align('left'),
        border: box(),
      })
      styleRange(sheet, r, 1, r, r === 7 ? totalCols - 1 : 3, {
        font: font({ bold: r !== 7, color: COLOR.navy }),
        alignment: align('left'),
        border: box(),
      })
    })

    // Banner: the block of "Lot Ini" is the only one in the primary colour
    const bannerStyle = (isLot) => ({
      font: font({ sz: 11, bold: true, color: COLOR.white }),
      fill: solid(isLot ? COLOR.blue : COLOR.slate),
      alignment: align('left'),
    })
    styleRange(
      sheet,
      bannerRow,
      0,
      bannerRow,
      leftCount - 1,
      bannerStyle(!isBackward),
    )
    styleRange(sheet, bannerRow, lineA, bannerRow, lineB, {
      font: font({ sz: 11, bold: true, color: COLOR.muted }),
      fill: solid(COLOR.gray),
      alignment: align('center'),
    })
    styleRange(
      sheet,
      bannerRow,
      rightStart,
      bannerRow,
      totalCols - 1,
      bannerStyle(isBackward),
    )

    // Column headers
    const headerStyle = (fill, color) => ({
      font: font({ bold: true, color }),
      fill: solid(fill),
      alignment: align('center', true),
      border: box(),
    })
    for (let c = 0; c < totalCols; c += 1) {
      styleCell(
        sheet,
        headerRow,
        c,
        isLotCol(c)
          ? headerStyle(COLOR.blueSoft, COLOR.blueDark)
          : headerStyle(COLOR.gray, COLOR.slate),
      )
    }

    // Data rows. Each group is boxed (thick outline) so the connected rows
    // read as one unit; the Lot Ini block is merged over the group and its
    // edge towards the lines is the "trunk" the arrows meet.
    const thin = { style: 'thin', color: { rgb: COLOR.line } }
    const outer = { style: 'medium', color: { rgb: COLOR.slate } }
    const trunk = { style: 'medium', color: { rgb: COLOR.blue } }
    groupInfo.forEach(({ start, end }) => {
      for (let r = start; r <= end; r += 1) {
        const idx = r - start
        for (let c = 0; c < totalCols; c += 1) {
          const header = allHeaders[c]
          const isLine = c === lineA || c === lineB
          const isNumber = header === 'Jumlah' || header === 'Jumlah Dipakai'
          const horizontal = isLine
            ? 'center'
            : isNumber
              ? 'right'
              : header === 'Satuan' || header === 'Status'
                ? 'center'
                : 'left'
          const statusColor =
            header === 'Status'
              ? STATUS_COLOR[tableRows[r - firstDataRow][c]]
              : undefined
          let fill = idx % 2 === 1 ? COLOR.zebra : COLOR.white
          if (isLotCol(c)) fill = COLOR.blueSoft
          if (isLine || (c >= midStart && c <= midEnd)) fill = COLOR.zebra
          const border = {
            top: idx === 0 ? outer : thin,
            bottom: r === end ? outer : thin,
            left: c === 0 ? outer : thin,
            right: c === totalCols - 1 ? outer : thin,
          }
          if (isBackward && c === rightStart) border.left = trunk
          if (!isBackward && c === leftCount - 1) border.right = trunk
          styleCell(sheet, r, c, {
            font: isLine
              ? font({ bold: true, color: COLOR.blue })
              : statusColor
                ? font({ bold: true, color: statusColor })
                : isLotCol(c)
                  ? font({ bold: true, color: COLOR.navy })
                  : font(),
            fill: solid(fill),
            alignment: align(horizontal, header === 'Keterangan'),
            border,
          })
        }
      }
    })

    // Footer
    styleRange(sheet, footerRow, 0, footerRow, totalCols - 1, {
      font: font({ sz: 9, color: COLOR.faint }),
      alignment: align('left'),
    })

    applyNumberFormat(sheet)
    XLSX.utils.book_append_sheet(wb, sheet, 'Ketelusuran')

    // =======================================================================
    // Sheet 2: Ringkasan Konversi
    // =======================================================================
    // One balance per unit (Set and Kg are never added together)
    const balances = summary.balances || []
    // Bahan Masuk = left block, Barang Jadi = right block of the flow
    const bahanSource = leftTitle
    const jadiSource = rightTitle
    const sumWidths = [40, 16, 10, 62]
    const sumCols = sumWidths.length
    const hasilBarOf = (pct) => {
      const filled = Math.round(
        (Math.max(0, Math.min(100, toNum(pct))) / 100) * 30,
      )
      return `${'█'.repeat(filled)}${'░'.repeat(30 - filled)}`
    }
    const balanceToneOf = (label) =>
      label === 'Susut'
        ? { fill: COLOR.orangeSoft, color: COLOR.orange }
        : label === 'Kelebihan' || label === 'Tidak Ada Pembanding'
          ? { fill: COLOR.gray, color: COLOR.slate }
          : { fill: COLOR.greenSoft, color: COLOR.green }

    const summaryRows = []
    const sumMerges = []
    const sumHeights = []
    const sumStyles = []
    const addRow = (cells = [], { merge = false, hpx, style } = {}) => {
      const index = summaryRows.length
      summaryRows.push(cells)
      if (merge) sumMerges.push(mergeRow(index, 0, sumCols - 1))
      if (hpx) sumHeights[index] = { hpx }
      if (style) sumStyles.push({ r: index, style })
      return index
    }
    const sectionStyle = (fill) => ({
      font: font({ sz: 11, bold: true, color: COLOR.white }),
      fill: solid(fill),
      alignment: align('left'),
    })
    const tableHeadStyle = {
      font: font({ bold: true, color: COLOR.white }),
      fill: solid(COLOR.slate),
      alignment: align('center'),
      border: box(COLOR.slate),
    }

    addRow(['RINGKASAN KONVERSI'], { merge: true, hpx: 34 })
    addRow([`${directionLabel}  ·  Lot ${lotCode}`], { merge: true, hpx: 24 })
    addRow([], { merge: true, hpx: 4 })
    addRow([summary.readingGuide || ''], {
      merge: true,
      hpx: 30,
      style: {
        font: font({ italic: true, color: COLOR.slate }),
        fill: solid(COLOR.blueSoft),
        alignment: align('left', true),
      },
    })
    addRow([], { hpx: 8 })

    // Neraca bahan
    addRow(['NERACA BAHAN'], {
      merge: true,
      hpx: 24,
      style: sectionStyle(COLOR.navy),
    })
    const tableHeadRow = addRow(['KETERANGAN', 'JUMLAH', 'SATUAN', 'CATATAN'], {
      hpx: 22,
    })
    const numberRows = []
    const barRows = []
    if (balances.length === 0) {
      numberRows.push({
        r: addRow(['Bahan Masuk', 0, '', 'Tidak ada data bahan.'], {
          hpx: 26,
        }),
        color: COLOR.slate,
        fill: COLOR.gray,
      })
    }
    balances.forEach((b, i) => {
      const unit = formatUnit(b.unit)
      if (i > 0) addRow([], { hpx: 6 })
      const tone = balanceToneOf(b.balanceLabel)
      numberRows.push(
        {
          r: addRow(
            [
              'Bahan Masuk',
              toNum(b.bahanMasuk),
              unit,
              `Bahan yang dipakai untuk membuat barang jadi (${bahanSource})`,
            ],
            { hpx: 26 },
          ),
          color: COLOR.slate,
          fill: COLOR.gray,
        },
        {
          r: addRow(
            [
              '−  Barang Jadi',
              toNum(b.barangJadi),
              unit,
              `Hasil dari bahan masuk (${jadiSource})`,
            ],
            { hpx: 26 },
          ),
          color: COLOR.slate,
          fill: COLOR.white,
        },
        {
          r: addRow(
            [
              `=  ${b.balanceLabel || 'Selisih'}`,
              b.comparable ? Math.abs(toNum(b.selisih)) : '-',
              b.comparable ? unit : '',
              b.comparable
                ? b.balanceHint || ''
                : `Hanya ada ${b.bahanMasuk > 0 ? 'bahan masuk' : 'barang jadi'} dengan satuan ini`,
            ],
            { hpx: 28 },
          ),
          color: tone.color,
          fill: tone.fill,
        },
      )
    })

    // Persentase barang jadi, per unit
    // Only units with both bahan and barang jadi have a percentage
    const withBahan = balances.filter((b) => b.comparable)
    if (withBahan.length > 0) {
      addRow([], { hpx: 8 })
      addRow(['PERSENTASE BARANG JADI'], {
        merge: true,
        hpx: 24,
        style: sectionStyle(COLOR.navy),
      })
      withBahan.forEach((b) => {
        numberRows.push({
          r: addRow(
            [
              withBahan.length > 1
                ? `Barang jadi dari bahan masuk (${formatUnit(b.unit)})`
                : 'Barang jadi dari bahan masuk',
              toNum(b.hasilPct),
              '%',
              '█ Barang jadi    ░ Selisih',
            ],
            { hpx: 28 },
          ),
          color: COLOR.slate,
          fill: COLOR.gray,
        })
        barRows.push(addRow([hasilBarOf(b.hasilPct)], { merge: true, hpx: 22 }))
      })
    }

    addRow([], { hpx: 8 })
    addRow([summary.sentence || ''], {
      merge: true,
      hpx: 28,
      style: {
        font: font({ bold: true, color: COLOR.slate }),
        fill: solid(COLOR.gray),
        alignment: align('left', true),
      },
    })
    addRow([], { hpx: 8 })

    // Rincian
    const breakdownBlocks = []
    const addBreakdown = (title, items, accent) => {
      addRow([`${title.toUpperCase()}  ·  ${items.length} jenis barang`], {
        merge: true,
        hpx: 24,
        style: sectionStyle(accent),
      })
      const head = addRow(['JENIS BARANG', 'JUMLAH', 'SATUAN'], { hpx: 22 })
      const first = summaryRows.length
      if (items.length > 0) {
        items.forEach((item) =>
          addRow([item.name, toNum(item.amount), formatUnit(item.unit)], {
            hpx: 22,
          }),
        )
      } else {
        addRow(['Tidak ada data barang.'], { hpx: 22 })
      }
      breakdownBlocks.push({
        head,
        first,
        last: summaryRows.length - 1,
        empty: items.length === 0,
      })
      addRow([], { hpx: 8 })
    }
    addBreakdown('Rincian Bahan Masuk', bahanItems, COLOR.slate)
    addBreakdown('Rincian Barang Jadi', barangJadiItems, COLOR.slate)

    const footerRowSum = addRow([createdAt], { merge: true })

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows)
    summarySheet['!cols'] = sumWidths.map((wch) => ({ wch }))
    summarySheet['!merges'] = sumMerges
    summarySheet['!rows'] = sumHeights

    styleTitleBlock(summarySheet, sumCols)
    sumStyles.forEach(({ r, style }) =>
      styleRange(summarySheet, r, 0, r, sumCols - 1, style),
    )
    styleRange(
      summarySheet,
      tableHeadRow,
      0,
      tableHeadRow,
      sumCols - 1,
      tableHeadStyle,
    )

    const numberRow = (r, color, fill) => {
      styleCell(summarySheet, r, 0, {
        font: font({ sz: 11, bold: true, color }),
        fill: solid(fill),
        alignment: align('left'),
        border: box(),
      })
      styleCell(summarySheet, r, 1, {
        font: font({ sz: 12, bold: true, color }),
        fill: solid(fill),
        alignment: align('right'),
        border: box(),
      })
      styleCell(summarySheet, r, 2, {
        font: font({ bold: true, color }),
        fill: solid(fill),
        alignment: align('center'),
        border: box(),
      })
      styleCell(summarySheet, r, 3, {
        font: font({ sz: 9, color: COLOR.muted }),
        fill: solid(fill),
        alignment: align('left', true),
        border: box(),
      })
    }
    numberRows.forEach(({ r, color, fill }) => numberRow(r, color, fill))
    barRows.forEach((r) =>
      styleRange(summarySheet, r, 0, r, sumCols - 1, {
        font: font({ sz: 12, color: COLOR.blue }),
        alignment: align('left'),
      }),
    )

    breakdownBlocks.forEach(({ head, first, last, empty }) => {
      styleRange(summarySheet, head, 0, head, 2, {
        font: font({ bold: true, color: COLOR.slate }),
        fill: solid(COLOR.gray),
        alignment: align('center'),
        border: box(),
      })
      for (let r = first; r <= last; r += 1) {
        const fill =
          (r - first) % 2 === 1 ? solid(COLOR.zebra) : solid(COLOR.white)
        styleCell(summarySheet, r, 0, {
          font: empty ? font({ italic: true, color: COLOR.faint }) : font(),
          fill,
          alignment: align('left'),
          border: box(),
        })
        styleCell(summarySheet, r, 1, {
          font: font({ bold: true }),
          fill,
          alignment: align('right'),
          border: box(),
        })
        styleCell(summarySheet, r, 2, {
          font: font(),
          fill,
          alignment: align('center'),
          border: box(),
        })
      }
    })
    styleRange(summarySheet, footerRowSum, 0, footerRowSum, sumCols - 1, {
      font: font({ sz: 9, color: COLOR.faint }),
      alignment: align('left'),
    })

    applyNumberFormat(summarySheet)
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Ringkasan Konversi')

    const excelBuffer = XLSX.write(wb, { bookType, type: 'array' })
    const finalData = new Blob([excelBuffer], { type: fileType })
    FileSaver.saveAs(
      finalData,
      `Laporan_Ketelusuran_${isBackward ? 'Asal' : 'Tujuan'}_${
        lotCode !== '-' ? lotCode : escrowpaymentId
      }_${moment().format('YYYY-MM-DD')}.xlsx`,
    )

    setTimeout(() => {
      setIsDownload(false)
    }, 2000)
  }, 300)
}
